/**
 * The open campaign GROUP's master config, kept small: one button in the
 * containers card header naming it. Its details open on demand — the
 * group's campaigns, the sections in a compact list, who follows, Edit,
 * Copy (the whole group, to paste on another board) and Delete (only once
 * no container follows it — a campaign group's containers always follow its
 * master config) — so the containers stay the page's focus.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, Crown, Pencil, Trash2 } from 'lucide-react';
import type { DropGenMasterSummary } from '@trafficpulse/shared';

import { Button, ConfirmDialog } from '@/components/ui';
import { CopyGroupButton } from '@/features/drop-generator/components/group-copy/clipboard/CopyGroupButton';
import { cn } from '@/lib/utils';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import { MasterFollowers } from './MasterFollowers';
import { followersOf, notFollowing, ruleTiles } from './rule-facts';
import { type GroupEntry, middleTruncate } from '../campaign-scope';
import { useMasterConfigActions } from './use-master-config-actions';

const followedNote = (n: number) =>
  `${n === 1 ? '1 container follows' : `${n} containers follow`} this master config — a campaign group's containers always follow it: move them to another campaign group or delete them first.`;

interface Props {
  master: DropGenMasterSummary;
  plan: DropGenPlanDetail;
  group: GroupEntry;
  onEdit: () => void;
}

export const MasterConfigButton: FC<Props> = ({ master, plan, group, onEdit }) => {
  const memberIds = new Set(group.members.map((m) => m.wiserId));
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const actions = useMasterConfigActions();
  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button type="button" className="flex h-9 min-w-0 max-w-[260px] items-center gap-2 rounded-md border border-input bg-background px-3 text-sm transition-colors hover:bg-accent" title={`Master config of ${group.fullLabel}`}>
            <Crown className="h-3.5 w-3.5 shrink-0" style={{ color: master.color }} />
            <span className="hidden shrink-0 text-muted-foreground sm:inline">Master config</span>
            <span className="truncate font-medium">{master.name}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="end" sideOffset={6} collisionPadding={12} onOpenAutoFocus={(e) => e.preventDefault()} className="z-[250] flex max-h-[var(--radix-popover-content-available-height)] w-[min(440px,92vw)] flex-col rounded-lg border border-border bg-popover shadow-lg">
            <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3">
              <Crown className="h-4 w-4 shrink-0" style={{ color: master.color }} />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-semibold">{master.name}</span>
                <span className="truncate text-xs text-muted-foreground" title={group.fullLabel}>
                  Master config · {middleTruncate(group.label, 34)} · {group.members.length} campaign{group.members.length === 1 ? '' : 's'}
                </span>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 px-4 py-3 text-xs">
                {ruleTiles(master).map((tile) => (
                  <div key={tile.key} className="contents">
                    <dt className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground">
                      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', tile.on ? 'bg-success' : 'bg-muted-foreground/40')} />
                      {tile.label}
                    </dt>
                    <dd className={cn('min-w-0', tile.on ? 'text-foreground' : 'text-muted-foreground')}>{tile.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="border-t border-border px-4 py-3">
                <MasterFollowers masterName={master.name} followers={followersOf(plan, master.id)} candidates={notFollowing(plan, memberIds)} following={actions.following} onFollow={(id) => actions.follow(id, master.id)} />
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-2.5">
              <Button
                variant="ghost"
                size="sm"
                disabled={master.followerCount > 0}
                title={master.followerCount > 0 ? followedNote(master.followerCount) : undefined}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => { setOpen(false); setDeleting(true); }}
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
              </Button>
              <span className="flex items-center gap-2">
                <CopyGroupButton countryKey={plan.countryKey} group={group} masterName={master.name} onCopied={() => setOpen(false)} />
                <Button size="sm" onClick={() => { setOpen(false); onEdit(); }}>
                  <Pencil className="mr-1 h-3.5 w-3.5" /> Edit master config
                </Button>
              </span>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <ConfirmDialog
        open={deleting}
        title={`Delete ${master.name}?`}
        description={master.followerCount > 0 ? followedNote(master.followerCount) : 'No container follows this master config. It is simply removed.'}
        confirmLabel="Delete master config"
        destructive
        busy={actions.removing}
        onConfirm={async () => (await actions.remove(master.id)) && setDeleting(false)}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
};
